/* FiaReleaseNudge — soft, ADHD-friendly nudge that gently reminds the user
 * to release a body lock-up if it's been 3+ days since the last release routine.
 *
 * Lives on the dashboard near MorningPrompt. Snoozable per day.
 * Clicking "Lös upp nu" switches to PT tab and opens the release modal
 * via a window CustomEvent which PTPage listens for. */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Bone, X, ArrowRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SNOOZE_KEY = 'fia_release_nudge_snoozed';

function todayKey() {
  return new Date().toISOString().slice(0, 10);
}

const AREA_LABELS = {
  rygg: 'ryggen',
  nacke: 'nacken',
  axlar: 'axlarna',
  höft: 'höften',
  käke: 'käken',
  handleder: 'handlederna',
  helkropp: 'kroppen',
};

export default function FiaReleaseNudge({ setActiveTab }) {
  const [status, setStatus] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        if (localStorage.getItem(SNOOZE_KEY) === todayKey()) {
          setDismissed(true);
          return;
        }
        const r = await axios.get(`${API}/pt/release-status`, { withCredentials: true });
        setStatus(r.data || null);
      } catch {/**/}
    };
    load();
  }, []);

  if (dismissed || !status || !status.suggest) return null;

  const days = status.days_since_last;
  const areaLabel = AREA_LABELS[status.last_area] || 'kroppen';
  const headline = !status.has_history
    ? 'Mjuk koll — har du knutar någonstans? 🦴'
    : days >= 7
      ? `Det är ${days} dagar sedan du löste upp ${areaLabel} 🦴`
      : `${days} dagar sedan ${areaLabel} fick lösas upp 🦴`;

  const snooze = () => {
    localStorage.setItem(SNOOZE_KEY, todayKey());
    setDismissed(true);
  };

  const open = () => {
    snooze();
    if (typeof setActiveTab === 'function') setActiveTab('pt');
    // Small delay to allow PTPage to mount and register its event listener
    setTimeout(() => {
      try {
        window.dispatchEvent(new CustomEvent('open-pt-release'));
      } catch {/**/}
    }, 150);
  };

  return (
    <div className="max-w-6xl mx-auto px-3 sm:px-4 pt-3" data-testid="fia-release-nudge">
      <div className="bg-gradient-to-r from-sky-100 via-cyan-100 to-teal-100 border-2 border-sky-300 rounded-2xl shadow-lg px-3 py-2.5 flex items-start gap-2.5">
        <Bone className="w-5 h-5 text-sky-600 mt-0.5 flex-shrink-0 animate-pulse" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-extrabold text-sky-900">{headline}</div>
          <p className="text-[11px] text-sky-800 leading-snug">
            Dr. Fia & {''}<b>Nova</b> har en 5-min mjuk rutin redo — andetag-takt, ingen knäck.
          </p>
          <div className="flex gap-1.5 mt-1.5">
            <button
              onClick={open}
              data-testid="fia-nudge-open"
              className="text-[11px] px-2.5 py-1 rounded-full bg-sky-500 hover:bg-sky-600 text-white font-bold flex items-center gap-1"
            >
              Lös upp nu <ArrowRight className="w-3 h-3" />
            </button>
            <button
              onClick={snooze}
              data-testid="fia-nudge-later"
              className="text-[11px] px-2.5 py-1 rounded-full bg-white/70 hover:bg-white text-sky-800 font-bold"
            >
              Senare
            </button>
          </div>
        </div>
        <button onClick={snooze} data-testid="fia-nudge-dismiss" className="text-sky-700 hover:text-sky-900 p-1" title="Inte idag">
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
